import { useMemo, useRef, useState } from 'react'
import { loanCapitalProgram } from '@/pages/pipeline-intelligence/dealResolution'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  Circle,
  ExternalLink,
  FileText,
  Lock,
  Mail,
  Phone,
  RefreshCw,
  Search,
  Upload,
  UserCheck,
} from 'lucide-react'
import { chartProgressGradient } from '@/config/chartTheme'
import { useAntarcticaDeals } from '@/hooks/useDemoDataAccess'
import { documentRequirements, type DocumentRequirement } from '@/services/documentRequirements'
import { documentsService } from '@/services/documents'
import { LOAN_STAGES, type Loan, type LoanStage } from '@/types'
import { formatUsd, formatUsdOrDash } from '@/utils/money'
import { borrowingEntity } from '@/pages/pipeline-intelligence/helpers'
import { borrowerSubmitted, daysWithUs } from '@/pages/borrowerTimeline'
import { parseDateValue } from '@/utils/dates'

/** What the screen shows for a figure the application does not carry. */
const NOT_PROVIDED = 'Not provided'

/** A value as stated, or an honest blank. Never a zero standing in for absence. */
function stated(value: string | number | null | undefined, render?: (v: never) => string): string {
  if (value === null || value === undefined || value === '') return NOT_PROVIDED
  if (typeof value === 'number' && !Number.isFinite(value)) return NOT_PROVIDED
  return render ? render(value as never) : String(value)
}

/**
 * A loan amount as the deal records it. `loans.loan_amount` is NOT NULL, so a
 * package that states no amount is stored as 0 and the API serves it as null,
 * which the Loan model reads back as 0 (loansService.withUnstatedAmount). That
 * 0 is "not stated", never a $0 request (Round 3 ruling).
 */
function statedAmount(amount: number | null | undefined): number | null {
  return typeof amount === 'number' && Number.isFinite(amount) && amount !== 0 ? amount : null
}

function pct(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return NOT_PROVIDED
  // Stored as a fraction on some deals and as a percent on others; a leverage
  // of 0.65 and one of 65 are the same number to a reader.
  const n = value <= 1 ? value * 100 : value
  return `${n.toFixed(1)}%`
}

function ratio(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return NOT_PROVIDED
  return `${value.toFixed(2)}x`
}

function count(value: number | null | undefined, unit: string): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return NOT_PROVIDED
  return `${value.toLocaleString()} ${unit}`
}

/** A whole number of days, or an honest blank. Zero is a real answer here. */
function days(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return NOT_PROVIDED
  const n = Math.max(0, Math.round(value))
  return `${n} day${n === 1 ? '' : 's'}`
}

/** A timestamp as a plain calendar date. The clock time is noise to a borrower. */
function asDate(value: string | null | undefined): string {
  // A bare "2026-08-03" is that calendar day, not UTC midnight (which the
  // desk's time zone reads as 2 August).
  const parsed = parseDateValue(value)
  if (!parsed) return NOT_PROVIDED
  return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

/**
 * What each stage means, told to the borrower rather than to the desk.
 *
 * A stage name alone ("Post-screen Quote") tells a borrower nothing about
 * whether the ball is in their court. `lender` says what is happening now;
 * `borrower` says the one thing they can do to shorten it. Both are keyed off
 * the deal's real stage — no stage is described that the deal is not in.
 */
const STAGE_GUIDANCE: Record<LoanStage, { lender: string; borrower: string }> = {
  Triage: {
    lender: 'Your request is being read and sized against our current appetite.',
    borrower:
      'Send the rent roll, T-12 and a sponsor bio now. Triage moves fastest on deals that can be sized the day they arrive.',
  },
  'Pre-screen Indication': {
    lender: 'We are pricing the request and checking it against programme limits.',
    borrower:
      'Fill any figure below that reads “Not provided” — an unpriced gap is what usually holds an indication.',
  },
  'Post-screen Quote': {
    lender: 'A quote is out with you. Credit is holding the file open against it.',
    borrower:
      'Come back on the quote, and start ordering third-party reports so they are not the long pole later.',
  },
  'Term Sheet Issued': {
    lender: 'The term sheet is with you for signature.',
    borrower:
      'Sign and return, and get the appraisal and environmental engaged — underwriting starts the day they are ordered.',
  },
  'Term Sheet Signed': {
    lender: 'Full underwriting is running: your figures are being verified against the documents.',
    borrower:
      'Answer diligence questions the same day they land. Every open item here is a day added to closing.',
  },
  'IC / Committee Approved': {
    lender: 'Committee has approved. The file is moving into closing.',
    borrower:
      'Get entity documents and insurance to us — those are what stall an approved deal at the closing table.',
  },
  'Rate Lock': {
    lender: 'Pricing is locked. Closing conditions are being cleared.',
    borrower: 'Clear any remaining condition below. The lock has a date on it.',
  },
  Funded: {
    lender: 'The loan has funded. Nothing further is required of you.',
    borrower: 'Your file stays open here for servicing and for your next request.',
  },
}

/**
 * One labelled figure from the application.
 *
 * An absent value is shown as absent and dimmed, never as a zero or a dash that
 * could be read as "nil" — the borrower is looking at this list precisely to
 * see what the underwriter is still missing.
 */
function Figure({ label, value }: { label: string; value: string }) {
  const missing = value === NOT_PROVIDED
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-amber-400/[0.06] py-1 last:border-b-0">
      <span className="text-[10px] uppercase tracking-[0.1em] text-stone-600">{label}</span>
      <span
        className={
          missing
            ? 'text-[11px] italic text-stone-600'
            : 'font-mono text-[11px] font-medium text-stone-200'
        }
      >
        {value}
      </span>
    </div>
  )
}

type PortalStatus = 'Active' | 'Documents Pending' | 'Under Review' | 'Funded'

interface BorrowerPortalRow {
  /** Stable row identity: the borrower name, or the deal for a nameless one. */
  key: string
  borrower: string
  /** False when the deal names no borrower — the row is still a real portal. */
  hasBorrowerOnRecord: boolean
  deals: Loan[]
  /** The stated amounts summed; null when no deal in the portal states one. */
  volume: number | null
  latestStage: LoanStage
  status: PortalStatus
  assignee: string
  launchDealId: string
}

function stageProgress(stage: LoanStage): number {
  const idx = LOAN_STAGES.indexOf(stage)
  if (idx < 0) return 0
  return Math.round(((idx + 1) / LOAN_STAGES.length) * 100)
}

function portalStatus(deals: Loan[]): PortalStatus {
  if (deals.every((d) => d.stage === 'Funded')) return 'Funded'
  if (deals.some((d) => d.documents.length === 0 && d.stage !== 'Funded')) return 'Documents Pending'
  if (deals.some((d) => d.stage === 'IC / Committee Approved' || d.stage === 'Term Sheet Issued' || d.stage === 'Term Sheet Signed')) {
    return 'Under Review'
  }
  return 'Active'
}

function StepIcon({ complete, active }: { complete: boolean; active: boolean }) {
  if (complete) return <CheckCircle2 size={14} className="flex-shrink-0 text-emerald-400" />
  if (active) return <RefreshCw size={14} className="flex-shrink-0 animate-spin text-amber-300" style={{ animationDuration: '3s' }} />
  return <Circle size={12} className="flex-shrink-0 text-stone-600" />
}

/**
 * The borrower's own workspace on their live application.
 *
 * It used to show a stage list, two filenames and a dropzone that was not
 * wired to anything — so the borrower could see that something was happening
 * and had no way to find out what the underwriter was still waiting on, nor
 * any way to send it. Most of the viewport was empty.
 *
 * The platform already knew the answer. `deal_diligence` keeps a per-deal
 * checklist of what a lender asks for on this asset type, reconciled against
 * the files that actually arrived, each item carrying the reason it is asked
 * for. That checklist is now the page: what is outstanding, why it matters,
 * and an upload beside each one. Beside it sits the application as the
 * underwriter currently holds it, so a borrower can see at a glance which
 * figures are missing and close those gaps too.
 *
 * Nothing here is invented. A figure the deal does not carry reads "Not
 * provided" rather than as a zero, because the whole purpose of the page is to
 * show the borrower exactly where the gaps are.
 */
function BorrowerLiveView({ deal, siblingDeals }: { deal: Loan; siblingDeals: Loan[] }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const stageIdx = LOAN_STAGES.indexOf(deal.stage)
  const progress = stageProgress(deal.stage)
  // A deal whose stage is not one we know still gets the opening advice rather
  // than a blank panel.
  const guidance = STAGE_GUIDANCE[deal.stage] ?? STAGE_GUIDANCE.Triage
  const funded = siblingDeals.filter((d) => d.stage === 'Funded')
  const [uploadNote, setUploadNote] = useState<string | null>(null)
  const [uploadingKey, setUploadingKey] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const pickerRef = useRef<HTMLInputElement | null>(null)
  const pendingRequirement = useRef<DocumentRequirement | null>(null)

  // What the lender needs from this borrower, reconciled against what arrived.
  const checklist = useQuery({
    queryKey: ['borrower-portal', 'requirements', deal.id],
    queryFn: () => documentRequirements.list(deal.id),
    staleTime: 30_000,
  })

  // What the lender actually holds for this deal. `deal.documents` is a JSON
  // column of names on the loan row that an upload never appends to, so a
  // borrower who had just sent two files was still told "nothing on the file
  // yet". The vault is the authority; the loan row's names are folded in
  // because correspondence attachments were only ever recorded there.
  const filesOnDeal = useQuery({
    queryKey: ['borrower-portal', 'documents', deal.id],
    queryFn: () => documentsService.listForLoan(deal.id),
    staleTime: 30_000,
  })

  const upload = useMutation({
    mutationFn: ({ file, requirement }: { file: File; requirement: DocumentRequirement | null }) =>
      documentsService.upload(deal.id, file, {
        category: requirement?.category ?? 'borrower-upload',
        notes: requirement ? `Provided for: ${requirement.label}` : undefined,
      }),
    onSuccess: async (_doc, variables) => {
      setUploadNote(
        variables.requirement
          ? `${variables.file.name} received for “${variables.requirement.label}”.`
          : `${variables.file.name} received.`,
      )
      // The checklist reconciles itself against the documents on the deal, so
      // re-reading it is what turns an upload into a tick.
      await queryClient.invalidateQueries({ queryKey: ['borrower-portal', 'requirements', deal.id] })
      await queryClient.invalidateQueries({ queryKey: ['borrower-portal', 'documents', deal.id] })
      await queryClient.invalidateQueries({ queryKey: ['loans'] })
    },
    onError: (failure) => {
      const detail = (failure as { response?: { data?: { detail?: unknown } } })?.response?.data
        ?.detail
      setUploadNote(typeof detail === 'string' ? detail : 'That file could not be uploaded.')
    },
    onSettled: () => setUploadingKey(null),
  })

  function choose(requirement: DocumentRequirement | null) {
    pendingRequirement.current = requirement
    setUploadingKey(requirement?.id ?? 'general')
    pickerRef.current?.click()
  }

  /** Files dragged onto the file card. Same path as the picker — no requirement
   *  is claimed for them, so the checklist matches them on their own merits. */
  function onDrop(event: React.DragEvent) {
    event.preventDefault()
    setDragOver(false)
    const files = Array.from(event.dataTransfer?.files ?? [])
    if (files.length === 0) return
    files.forEach((file) => upload.mutate({ file, requirement: null }))
  }

  function onFilesPicked(event: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? [])
    const requirement = pendingRequirement.current
    if (files.length === 0) {
      setUploadingKey(null)
      return
    }
    files.forEach((file) => upload.mutate({ file, requirement }))
    // Reset so choosing the same file twice still fires a change event.
    event.target.value = ''
  }

  const items = checklist.data?.items ?? []
  const outstanding = items.filter((i) => i.status === 'outstanding' && i.required)
  const optionalOutstanding = items.filter((i) => i.status === 'outstanding' && !i.required)
  const received = items.filter((i) => i.status === 'received')
  const waived = items.filter((i) => i.status === 'waived')
  // The badge counts REQUIRED items only. Counting optional ones alongside them
  // read as "0 of 31" against a column saying "24 outstanding", which is the
  // kind of arithmetic a borrower should not have to do.
  const requiredItems = items.filter((i) => i.required)
  const requiredIn = requiredItems.filter((i) => i.status !== 'outstanding')
  const optionalIn = optionalOutstanding.length

  // Grouped by the category the lender files it under, so a borrower gathering
  // sponsor paperwork is not bouncing between property and entity items.
  const outstandingByCategory = useMemo(() => {
    const groups = new Map<string, DocumentRequirement[]>()
    ;[...outstanding, ...optionalOutstanding].forEach((item) => {
      const list = groups.get(item.category) ?? []
      list.push(item)
      groups.set(item.category, list)
    })
    return Array.from(groups.entries())
  }, [outstanding, optionalOutstanding])

  const completion = checklist.data?.completionPct

  const onFile = useMemo(() => {
    const seen = new Map<string, { filename: string; receivedAt: string | null }>()
    ;(filesOnDeal.data ?? []).forEach((doc) =>
      seen.set(doc.filename, { filename: doc.filename, receivedAt: doc.createdAt ?? null }),
    )
    deal.documents.forEach((filename) => {
      if (!seen.has(filename)) seen.set(filename, { filename, receivedAt: null })
    })
    return Array.from(seen.values()).sort((a, b) => (b.receivedAt ?? '').localeCompare(a.receivedAt ?? ''))
  }, [filesOnDeal.data, deal.documents])

  return (
    <div className="page-fill gap-2 p-3">
      <input
        ref={pickerRef}
        type="file"
        multiple
        className="hidden"
        onChange={onFilesPicked}
        aria-hidden
      />

      {/* The page reads as the borrower will see it, but no borrower can reach
          it: the route sits behind the staff sign-in (nd-025). Say so on the
          page itself, not only on the admin list. */}
      <div
        role="note"
        className="flex-shrink-0 rounded-md border border-amber-400/25 bg-amber-400/[0.06] px-3 py-1.5 text-[11px] text-amber-200"
      >
        <span className="font-semibold">Staff preview</span> — this is the page a borrower would see.
        Borrowers cannot open this page yet: it requires a staff sign-in, so only staff see it.
      </div>

      {/* Header — what this is, and the one number that matters to a borrower. */}
      <div className="flex flex-shrink-0 flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="terminal-kicker">Borrower Portal</div>
          <p className="mt-0.5 truncate text-[11px] text-stone-500">
            {borrowingEntity(deal) || deal.property} — live application workspace
          </p>
        </div>
        <div className="flex items-center gap-2">
          {items.length > 0 && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/20 bg-amber-400/[0.06] px-2.5 py-0.5 text-[10px] text-amber-200">
              {requiredIn.length} of {requiredItems.length} required items in
              {optionalIn > 0 && (
                <span className="text-stone-500">· {optionalIn} optional open</span>
              )}
            </span>
          )}
          <span className="inline-flex items-center gap-1 rounded-full border border-amber-400/20 bg-amber-400/[0.06] px-2.5 py-0.5 text-[10px] text-amber-200">
            <Lock size={9} /> Staff only
          </span>
          <button
            type="button"
            onClick={() => navigate('/borrower-portal')}
            className="btn-secondary flex items-center gap-1.5 text-[11px]"
          >
            <ArrowLeft size={12} /> Admin
          </button>
        </div>
      </div>

      <div className="grid min-h-0 flex-1 gap-2 overflow-hidden xl:grid-cols-[1.35fr_1fr_1fr]">
        {/* ── What we still need ─────────────────────────────────────────── */}
        <div className="card flex min-h-0 flex-col overflow-hidden">
          <div className="flex flex-shrink-0 items-start justify-between gap-3 border-b border-amber-400/10 px-4 py-2.5">
            <div>
              <span className="terminal-kicker">What we still need from you</span>
              <p className="mt-0.5 text-[11px] text-stone-500">
                {outstanding.length === 0
                  ? 'Nothing outstanding — your package is complete.'
                  : `${outstanding.length} required item${outstanding.length === 1 ? '' : 's'} outstanding. Each one is something the underwriter cannot proceed without.`}
              </p>
            </div>
            <button
              type="button"
              onClick={() => choose(null)}
              disabled={upload.isPending}
              className="btn-secondary flex flex-shrink-0 items-center gap-1.5 text-[10px] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Upload size={11} /> Upload
            </button>
          </div>

          {/* How complete the package is, as the checklist scores it. Null means
              nothing has been asked of the deal yet — unstarted, not finished —
              so no bar is drawn rather than an encouraging empty one. */}
          {completion !== null && completion !== undefined && (
            <div className="flex flex-shrink-0 items-center gap-2 border-b border-amber-400/10 px-4 py-1.5">
              <span className="text-[9px] uppercase tracking-[0.14em] text-stone-600">
                Package
              </span>
              <div className="chart-viz-track chart-viz-bar--rounded h-1.5 flex-1">
                <div
                  className="chart-viz-bar chart-viz-bar--rounded h-full"
                  style={{ width: `${completion}%`, background: chartProgressGradient(0) }}
                />
              </div>
              <span className="font-mono text-[10px] font-semibold text-amber-300">
                {Math.round(completion)}%
              </span>
            </div>
          )}

          {uploadNote && (
            <div className="flex-shrink-0 border-b border-amber-400/10 bg-amber-400/[0.04] px-4 py-1.5 text-[10px] text-amber-200">
              {uploadNote}
            </div>
          )}

          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto scrollbar-thin px-4 py-3">
            {checklist.isLoading && (
              <p className="text-[11px] text-stone-500">Reading the checklist for this deal…</p>
            )}
            {checklist.isError && (
              <p className="text-[11px] text-red-300">
                The checklist could not be loaded. Your originator can send it to you directly.
              </p>
            )}
            {!checklist.isLoading && !checklist.isError && items.length === 0 && (
              <p className="text-[11px] text-stone-500">
                No document list has been drawn up for this deal yet. Anything you send now is still
                attached to the file.
              </p>
            )}

            {outstandingByCategory.map(([category, group]) => (
              <div key={category}>
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-[0.14em] text-stone-600">
                    {category}
                  </span>
                  <span className="h-px flex-1 bg-amber-400/10" />
                </div>
                <div className="space-y-1.5">
                  {group.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-start gap-2.5 rounded-lg border border-amber-400/10 bg-black/20 px-3 py-2"
                    >
                      <AlertCircle
                        size={13}
                        className={
                          item.required
                            ? 'mt-0.5 flex-shrink-0 text-amber-400'
                            : 'mt-0.5 flex-shrink-0 text-stone-600'
                        }
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-[12px] font-medium text-stone-100">
                            {item.label}
                          </span>
                          {!item.required && (
                            <span className="rounded border border-stone-500/20 px-1.5 text-[9px] text-stone-500">
                              Optional
                            </span>
                          )}
                        </div>
                        {/* Why a lender asks for it — the difference between a
                            checklist a borrower works through and one they
                            ignore. */}
                        {item.purpose && (
                          <p className="mt-0.5 text-[10px] leading-snug text-stone-500">
                            {item.purpose}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => choose(item)}
                        disabled={upload.isPending}
                        className="flex flex-shrink-0 items-center gap-1 rounded border border-amber-400/20 px-2 py-1 text-[10px] text-amber-200 transition-colors hover:bg-amber-400/10 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {uploadingKey === item.id && upload.isPending ? (
                          <RefreshCw size={9} className="animate-spin" />
                        ) : (
                          <Upload size={9} />
                        )}
                        Provide
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {received.length > 0 && (
              <div>
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-[0.14em] text-emerald-500/70">
                    Received
                  </span>
                  <span className="h-px flex-1 bg-emerald-400/10" />
                </div>
                <div className="space-y-1">
                  {received.map((item) => (
                    <div key={item.id} className="flex items-start gap-2.5 px-1 py-1">
                      <CheckCircle2 size={13} className="mt-0.5 flex-shrink-0 text-emerald-400" />
                      <div className="min-w-0 flex-1">
                        <div className="text-[11px] text-stone-300">{item.label}</div>
                        {/* Named, because a tick with no file behind it is a
                            claim the borrower cannot check. */}
                        {item.satisfiedBy.length > 0 && (
                          <div className="truncate text-[10px] text-stone-600">
                            {item.satisfiedBy.map((e) => e.filename).join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {waived.length > 0 && (
              <div>
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-[0.14em] text-stone-600">
                    Waived by the lender
                  </span>
                  <span className="h-px flex-1 bg-amber-400/10" />
                </div>
                <div className="space-y-1">
                  {waived.map((item) => (
                    <div key={item.id} className="px-1 py-0.5 text-[11px] text-stone-500">
                      {item.label}
                      {item.waivedReason ? ` — ${item.waivedReason}` : ''}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── The application as the underwriter holds it ────────────────── */}
        <div className="card flex min-h-0 flex-col overflow-hidden">
          <div className="flex-shrink-0 border-b border-amber-400/10 px-4 py-2.5">
            <span className="terminal-kicker">Your application</span>
            <p className="mt-0.5 text-[11px] text-stone-500">
              What the underwriter holds today. Anything reading “Not provided” is a gap you can
              close.
            </p>
          </div>
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto scrollbar-thin px-4 py-3">
            <div>
              <div className="mb-1 text-[10px] uppercase tracking-[0.14em] text-stone-600">
                Request
              </div>
              <Figure label="Loan amount" value={stated(statedAmount(deal.loanAmount), formatUsd)} />
              {/* The capital program IS the product. This printed the property
                  type ("Multifamily") as the product when none was stored. */}
              <Figure label="Product" value={stated(loanCapitalProgram(deal))} />
              <Figure label="Rate" value={stated(deal.rate)} />
              <Figure label="Maturity" value={stated(deal.maturity)} />
            </div>

            <div>
              <div className="mb-1 text-[10px] uppercase tracking-[0.14em] text-stone-600">
                Leverage &amp; coverage
              </div>
              <Figure label="LTV" value={pct(deal.ltv)} />
              <Figure label="DSCR" value={ratio(deal.dscr)} />
              <Figure label="Debt yield" value={pct(deal.debtYield)} />
              <Figure label="NOI" value={deal.noi ? formatUsd(deal.noi) : NOT_PROVIDED} />
              <Figure
                label="Property value"
                value={deal.propertyValue ? formatUsd(deal.propertyValue) : NOT_PROVIDED}
              />
            </div>

            <div>
              <div className="mb-1 text-[10px] uppercase tracking-[0.14em] text-stone-600">
                Property
              </div>
              <Figure label="Asset" value={stated(deal.property)} />
              <Figure label="Location" value={stated(deal.location)} />
              <Figure label="Type" value={stated(deal.type)} />
              <Figure label="Units" value={count(deal.units, 'units')} />
              <Figure label="Building area" value={count(deal.sf, 'SF')} />
              <Figure label="Year built" value={stated(deal.yearBuilt)} />
            </div>

            <div>
              <div className="mb-1 text-[10px] uppercase tracking-[0.14em] text-stone-600">
                Parties
              </div>
              <Figure label="Borrower" value={stated(borrowingEntity(deal))} />
              <Figure label="Sponsor" value={stated(deal.sponsor)} />
              <Figure label="Broker" value={stated(deal.brokerName)} />
              <Figure label="Lender / capital" value={stated(deal.investor)} />
            </div>

            {/* How long this has taken so far. A borrower chasing a closing date
                is entitled to the same clock the desk is watching. */}
            <div>
              <div className="mb-1 text-[10px] uppercase tracking-[0.14em] text-stone-600">
                Timeline
              </div>
              {/* The day the package was received, not the day the platform
                  ingested it (nd3-40). */}
              <Figure label="Submitted" value={asDate(borrowerSubmitted(deal))} />
              <Figure label="With us" value={days(daysWithUs(deal))} />
              <Figure label="In this stage" value={days(deal.daysInStatus)} />
              {deal.stage === 'Funded' && (
                <Figure label="Funded" value={asDate(deal.fundedAt)} />
              )}
            </div>
          </div>

          {/* What is happening right now, and the one thing that shortens it. */}
          <div className="flex-shrink-0 border-t border-amber-400/10 bg-amber-400/[0.03] px-4 py-2.5">
            <div className="text-[10px] uppercase tracking-[0.14em] text-stone-600">
              What happens next
            </div>
            <p className="mt-1 text-[11px] leading-relaxed text-stone-300">{guidance.lender}</p>
            <p className="mt-1 flex gap-1.5 text-[11px] leading-relaxed text-amber-200/90">
              <AlertCircle size={11} className="mt-[3px] flex-shrink-0" />
              <span>{guidance.borrower}</span>
            </p>
          </div>
        </div>

        {/* ── Where it stands, and who to ask ────────────────────────────── */}
        <div className="flex min-h-0 flex-col gap-2 overflow-hidden">
          <div className="card flex flex-shrink-0 flex-col overflow-hidden px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-mono text-[10px] text-stone-600">{deal.id}</div>
                <div className="mt-0.5 truncate text-[13px] font-semibold text-stone-100">
                  {deal.property}
                </div>
              </div>
              <span className="inline-flex flex-shrink-0 rounded-full border border-amber-400/25 bg-amber-400/[0.08] px-2 py-0.5 text-[9px] font-medium uppercase tracking-[0.12em] text-amber-200">
                {deal.stage}
              </span>
            </div>

            <div className="mt-2.5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-[0.14em] text-stone-600">
                  Progress
                </span>
                <span className="font-mono text-[11px] font-semibold text-amber-300">
                  {progress}%
                </span>
              </div>
              <div className="chart-viz-track chart-viz-bar--rounded mt-1.5 h-2">
                <div
                  className="chart-viz-bar chart-viz-bar--rounded h-full"
                  style={{ width: `${progress}%`, background: chartProgressGradient(0) }}
                />
              </div>
            </div>

            <div className="mt-2.5 space-y-1">
              {LOAN_STAGES.map((stage, i) => (
                <div key={stage} className="flex items-center gap-2.5">
                  <StepIcon complete={i < stageIdx} active={i === stageIdx} />
                  <span
                    className={`text-[11px] ${i < stageIdx ? 'text-stone-300' : i === stageIdx ? 'font-medium text-amber-200' : 'text-stone-600'}`}
                  >
                    {stage}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Everything we hold. This card takes the slack in the column, so an
              empty file is an invitation to send something rather than a void. */}
          <div className="card flex min-h-0 flex-1 flex-col overflow-hidden px-4 py-2.5">
            <div className="flex flex-shrink-0 items-center justify-between gap-2">
              <span className="terminal-kicker">Your file</span>
              <span className="font-mono text-[10px] text-stone-500">
                {onFile.length} on file
              </span>
            </div>
            {onFile.length === 0 ? (
              <button
                type="button"
                onClick={() => choose(null)}
                disabled={upload.isPending}
                onDragOver={(e) => {
                  e.preventDefault()
                  setDragOver(true)
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                className={`mt-2 flex min-h-0 flex-1 flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed px-3 py-4 text-center transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                  dragOver
                    ? 'border-amber-400/60 bg-amber-400/[0.08]'
                    : 'border-amber-400/20 hover:border-amber-400/40 hover:bg-amber-400/[0.04]'
                }`}
              >
                <Upload size={16} className="text-amber-400/70" />
                <span className="text-[11px] font-medium text-stone-300">
                  {upload.isPending ? 'Sending…' : 'Nothing on the file yet'}
                </span>
                <span className="text-[10px] leading-relaxed text-stone-500">
                  Drop files here, or click to browse. Anything you already have helps — it does not
                  have to match a checklist item.
                </span>
              </button>
            ) : (
              <div className="mt-1.5 min-h-0 flex-1 space-y-1 overflow-y-auto scrollbar-thin">
                {onFile.map((doc) => (
                  <div key={doc.filename} className="flex items-baseline gap-2">
                    <FileText
                      size={11}
                      className="flex-shrink-0 translate-y-[2px] text-amber-400/70"
                    />
                    <span className="min-w-0 flex-1 truncate text-[11px] text-stone-400">
                      {doc.filename}
                    </span>
                    {doc.receivedAt && (
                      <span className="flex-shrink-0 font-mono text-[9px] text-stone-600">
                        {asDate(doc.receivedAt)}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {funded.length > 0 && (
            <div className="card flex-shrink-0 px-4 py-2.5">
              <div className="terminal-kicker">Funded with us</div>
              <div className="mt-1.5 max-h-20 space-y-1 overflow-y-auto scrollbar-thin">
                {funded.map((loan) => (
                  <div key={loan.id} className="flex items-center justify-between gap-2">
                    <span className="truncate text-[11px] text-amber-300">{loan.property}</span>
                    <span className="flex-shrink-0 font-mono text-[11px] text-stone-400">
                      {formatUsdOrDash(statedAmount(loan.loanAmount))}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="card flex-shrink-0 px-4 py-2.5">
            <div className="terminal-kicker">Your originator</div>
            <div className="mt-1.5 flex items-center gap-2.5">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-amber-400/15 bg-amber-400/[0.06]">
                <UserCheck size={14} className="text-amber-300" />
              </div>
              <div className="min-w-0">
                <div className="truncate text-[12px] font-semibold text-stone-100">
                  {deal.assignee || 'Not yet assigned'}
                </div>
                <div className="flex items-center gap-2.5 text-[10px] text-stone-500">
                  <span className="flex items-center gap-1">
                    <Mail size={9} /> originations
                  </span>
                  <span className="flex items-center gap-1">
                    <Phone size={9} /> via your broker
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function BorrowerAdmin({ deals }: { deals: Loan[] }) {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')

  const portals = useMemo<BorrowerPortalRow[]>(() => {
    // Grouped by borrower, because one borrower with three deals is one portal
    // and one link. What is NOT a group is the deals with no borrower on
    // record: they were all keyed on the same "Unnamed borrower" string, so
    // every nameless deal on the book collapsed into a single row, one link
    // and one summed volume — eleven deals showing as five portals, and four
    // properties hidden behind an ellipsis on a row that belonged to nobody.
    // A deal whose borrower is unknown is its own portal, keyed on the deal.
    const byBorrower = new Map<string, Loan[]>()
    deals.forEach((d) => {
      const named = borrowingEntity(d)
      const key = named || `deal:${d.id}`
      const list = byBorrower.get(key) ?? []
      list.push(d)
      byBorrower.set(key, list)
    })
    return Array.from(byBorrower.entries())
      .map(([key, rows]) => {
        const latest = rows.reduce((a, b) => (LOAN_STAGES.indexOf(b.stage) > LOAN_STAGES.indexOf(a.stage) ? b : a))
        const named = borrowingEntity(rows[0])
        return {
          key,
          // Named for the deal it belongs to, so a portal with no borrower on
          // record is still identifiable rather than anonymous.
          borrower: named || `${latest.property} — borrower not on record`,
          hasBorrowerOnRecord: Boolean(named),
          deals: rows,
          volume: rows.some((d) => statedAmount(d.loanAmount) != null)
            ? rows.reduce((s, d) => s + (statedAmount(d.loanAmount) ?? 0), 0)
            : null,
          latestStage: latest.stage,
          status: portalStatus(rows),
          assignee: latest.assignee,
          launchDealId: latest.id,
        }
      })
      .sort((a, b) => (b.volume ?? 0) - (a.volume ?? 0))
  }, [deals])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return portals
    return portals.filter(
      (p) =>
        p.borrower.toLowerCase().includes(q) ||
        p.deals.some((d) => d.property.toLowerCase().includes(q) || d.id.toLowerCase().includes(q)),
    )
  }, [portals, query])

  // No link is copied for a borrower: /borrower-portal/live/:dealId sits
  // behind the staff sign-in and no borrower-facing endpoint exists, so a
  // copied URL sent to a borrower opened the Microsoft Entra login page.
  const launch = (dealId: string) => {
    navigate(`/borrower-portal/live/${encodeURIComponent(dealId)}`)
  }

  return (
    <div className="page-fill gap-2 p-3">
      <div className="flex flex-shrink-0 items-center justify-between gap-3">
        <div>
          <div className="terminal-kicker">Borrower Portal Admin</div>
          <p className="mt-0.5 text-[11px] text-stone-500">
            Manage every borrower workspace and open its live page. Staff sign-in required —
            borrowers cannot open these pages yet.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 rounded-md border border-amber-400/10 bg-black/40 px-2.5 py-1">
            <Search size={12} className="text-stone-600" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search borrower, property, deal…"
              className="w-56 bg-transparent text-[11px] text-stone-300 outline-none placeholder:text-stone-600"
            />
          </div>
        </div>
      </div>

      <div className="grid flex-shrink-0 grid-cols-4 gap-2">
        <div className="metric-cell">
          <div className="metric-cell-label">Borrower portals</div>
          <div className="metric-cell-value">{portals.length}</div>
        </div>
        <div className="metric-cell">
          <div className="metric-cell-label">Active deals</div>
          <div className="metric-cell-value">{deals.filter((d) => d.stage !== 'Funded').length}</div>
        </div>
        <div className="metric-cell">
          <div className="metric-cell-label">Funded</div>
          <div className="metric-cell-value">{deals.filter((d) => d.stage === 'Funded').length}</div>
        </div>
        <div className="metric-cell">
          <div className="metric-cell-label">Total volume</div>
          <div className="metric-cell-value">{formatUsdOrDash(statedAmount(deals.reduce((sum, d) => sum + (statedAmount(d.loanAmount) ?? 0), 0)))}</div>
        </div>
      </div>

      <div className="card flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex-shrink-0 border-b border-amber-400/10 px-4 py-2">
          <span className="terminal-kicker">All borrower portals</span>
          <span className="ml-2 text-[10px] text-stone-600">{filtered.length} shown</span>
        </div>
        <div className="min-h-0 flex-1 overflow-auto scrollbar-thin">
          {filtered.length === 0 ? (
            <div className="flex h-full items-center justify-center px-4 text-center text-[12px] text-stone-500">
              No borrowers in the pipeline yet. Promote a deal from Intake and it will appear here.
            </div>
          ) : (
            <table className="dense-table">
              <thead>
                <tr>
                  <th>Borrower</th>
                  <th>Properties</th>
                  <th className="text-right">Volume</th>
                  <th>Latest stage</th>
                  <th>Status</th>
                  <th>Originator</th>
                  <th className="text-right">Launch</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr key={row.key}>
                    <td
                      className={
                        row.hasBorrowerOnRecord
                          ? 'font-medium text-stone-100'
                          : 'font-medium text-stone-400 italic'
                      }
                    >
                      {row.borrower}
                    </td>
                    <td className="max-w-[240px] truncate text-stone-400">
                      {row.deals.map((d) => d.property).join(', ')}
                    </td>
                    <td className="text-right font-mono">{formatUsdOrDash(row.volume)}</td>
                    <td className="text-stone-300">{row.latestStage}</td>
                    <td>
                      <span className="rounded-full border border-amber-400/20 bg-amber-400/[0.06] px-2 py-0.5 text-[10px] text-amber-200">
                        {row.status}
                      </span>
                    </td>
                    <td className="text-stone-400">{row.assignee || '—'}</td>
                    <td className="text-right">
                      <div className="flex justify-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => launch(row.launchDealId)}
                          className="inline-flex items-center gap-1 rounded border border-amber-400/30 bg-amber-400/[0.08] px-2 py-1 text-[10px] text-amber-200 hover:bg-amber-400/15"
                        >
                          <ExternalLink size={10} />
                          Launch
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}

export default function BorrowerPortal() {
  const { dealId } = useParams<{ dealId?: string }>()
  const deals = useAntarcticaDeals('borrower-portal')
  const liveDeal = dealId ? deals.find((d) => d.id === decodeURIComponent(dealId)) : undefined

  if (dealId) {
    if (!liveDeal) {
      return (
        <div className="page-fill items-center justify-center p-6">
          <div className="max-w-md text-center">
            <p className="text-[13px] font-medium text-stone-200">Borrower portal not found</p>
            <p className="mt-1.5 text-[11px] text-stone-500">
              This launch link does not match a deal in the current pipeline.
            </p>
          </div>
        </div>
      )
    }
    // Only a NAMED borrower has siblings. Matching on an empty string made
    // every deal that names no borrower a sibling of every other, so one
    // borrower's "Funded with us" listed strangers' loans.
    const siblingDeals = borrowingEntity(liveDeal)
      ? deals.filter((d) => borrowingEntity(d) === borrowingEntity(liveDeal))
      : [liveDeal]
    return <BorrowerLiveView deal={liveDeal} siblingDeals={siblingDeals} />
  }

  return <BorrowerAdmin deals={deals} />
}
